import { StaffingRow, StaffingUser } from '../Types/staffing';

// History records the replacement; the current roster determines whether it still applies.
export function staffingReplacements(row: StaffingRow) {
	const substitutes = staffingPeople(row).substitutes;
	const seen = new Set<number>();
	return [...row.history]
		.sort((a, b) => b.id - a.id)
		.flatMap((entry) => {
			if (!['replace', 'restore', 'remove'].includes(entry.action) || seen.has(entry.userId)) return [];
			seen.add(entry.userId);
			if (entry.action !== 'replace') return [];
			const original = row.people.find((p) => p.id === entry.userId && p.isAbsent);
			const replacement = substitutes.find((p) => p.id === entry.replacementId);
			return original && replacement ? [{ original, replacement }] : [];
		});
}

export function staffingPeople(row: StaffingRow) {
	const absent = (p: StaffingUser) => p.isAbsent || row.unresolvedUserIds.includes(p.id);
	const available = (p: StaffingUser) => p.isActive && !p.isDeleted && !absent(p);
	return {
		regular: row.people.filter((p) => available(p) && !p.isSubstitute),
		absent: row.people.filter(absent),
		pending: row.people.filter((p) => row.leaves?.some((l) => l.userId === p.id && !l.approved)),
		substitutes: row.people.filter((p) => available(p) && p.isSubstitute),
	};
}

export const canAssignStaffing = (row: StaffingRow) =>
	row.canWrite && (row.current < row.required || row.projected < row.required || row.missingRegular);

// Replacing unresolved leave records absence atomically. Existing absence only needs an assignment.
export function staffingAssignmentTarget(row: Pick<StaffingRow, 'unresolvedUserIds'>, personId: number) {
	return row.unresolvedUserIds.includes(personId)
		? { action: 'replace' as const, userId: personId }
		: { action: 'assign' as const, userId: 0 };
}

// Visibility only: the API already restricts the returned events to the user's access.
export function staffingCalendarKind(
	event: { mine: boolean; canJoin: boolean; absent: boolean; isSubstitute: boolean; showInOverview?: boolean },
	showAllEvents = event.showInOverview === true
): 'mine' | 'absent' | 'substitute' | 'available' | 'other' | null {
	if (event.mine) return event.absent ? 'absent' : event.isSubstitute ? 'substitute' : 'mine';
	if (event.canJoin) return 'available';
	return showAllEvents ? 'other' : null;
}

export const canSelectLeaveStaffing = (row: StaffingRow, userId: number) =>
	row.canWrite &&
	(row.unresolvedUserIds.includes(userId) ||
		(row.leaves.some((l) => l.userId === userId && l.approved) &&
			row.people.some((p) => p.id === userId && p.isAbsent) &&
			canAssignStaffing(row)));
